import { useMemo, useRef, useState } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { ModalBottom } from '@ui/components/Modal'
import Button from '@ui/buttons/Button'
import Typography from '@ui/typography'
import FormattedText from '@ui/typography/FormattedText'
import Gradient from '@ui/effects/Gradient'
import { colors } from '@ui/colors'
import { DEFAULT_REGION_POLICIES } from '@ca/common'
import useCreateCustomerStore from './useCreateCustomerStore'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { CreateStepKey } from './types'
import StepDots from './StepDots'
import {
  createStepValidationContext,
  getModalSteps,
  getStepStatus,
  isKidsAccount,
} from './utils'
import PersonalStep from './steps/PersonalStep'
import ContactStep from './steps/ContactStep'
import AddressStep from './steps/AddressStep'
import InvoiceStep from './steps/InvoiceStep'
import PoliciesStep from './steps/PoliciesStep'

interface Props {
  open: boolean
  close: () => void
  initialStep?: CreateStepKey
  onAllConfirmed?: () => void
  errorSections?: Set<CreateStepKey>
}

interface StepMeta {
  key: CreateStepKey
  titleKey: string
  descKey: string
}

const STEP_META: Record<CreateStepKey, StepMeta> = {
  personal: {
    key: 'personal',
    titleKey: 'modals:personal.title',
    descKey: 'modals:personal.desc',
  },
  contact: {
    key: 'contact',
    titleKey: 'modals:contact.title',
    descKey: 'modals:contact.desc',
  },
  address: {
    key: 'address',
    titleKey: 'modals:address.title',
    descKey: 'modals:address.desc',
  },
  invoice: {
    key: 'invoice',
    titleKey: 'modals:invoice.title',
    descKey: 'modals:invoice.desc',
  },
  policies: {
    key: 'policies',
    titleKey: 'modals:policies.title',
    descKey: 'modals:policies.desc',
  },
}

const KIDS_DESC_KEYS: Partial<Record<CreateStepKey, string>> = {
  contact: 'contact.parentDesc',
  address: 'address.parentDesc',
  invoice: 'invoice.parentDesc',
}

export default function CreateCustomerModal({
  open,
  close,
  initialStep,
  onAllConfirmed,
  errorSections,
}: Props) {
  const { t } = useTranslation(['modals', 'ui'])
  const { data } = useCreateCustomerStore()
  const config = useRemoteConfig()

  const kids = isKidsAccount(data)
  const steps = useMemo(
    () => getModalSteps(data, initialStep),
    [data, initialStep]
  )

  const regionPolicies = config?.region.policies ?? DEFAULT_REGION_POLICIES
  const validationCtx = createStepValidationContext(
    data,
    regionPolicies,
    config?.region?.vat !== 'hidden'
  )

  const stepStatuses = useMemo(
    () =>
      steps.map((key) => {
        if (errorSections?.has(key)) {
          return 'danger' as const
        }
        return getStepStatus(key, data, validationCtx)
      }),
    [steps, data, validationCtx, errorSections]
  )

  const initialIndex = initialStep ? steps.indexOf(initialStep) : 0

  const [stepIndex, setStepIndex] = useState(
    initialIndex >= 0 ? initialIndex : 0
  )
  const [isPending, setIsPending] = useState(false)

  const submitRef = useRef<() => void>(() => undefined)

  const isLast = stepIndex === steps.length - 1
  const stepKey = steps[stepIndex]
  const step = STEP_META[stepKey]

  const stepDescKey =
    kids && KIDS_DESC_KEYS[stepKey]
      ? KIDS_DESC_KEYS[stepKey]
      : step.descKey.replace('modals:', '')

  const stepDescUsesFormatting = kids && !!KIDS_DESC_KEYS[stepKey]

  const handleStepSuccess = () => {
    setIsPending(false)
    if (isLast) {
      onAllConfirmed?.()
      close()
    } else {
      setStepIndex((i) => i + 1)
    }
  }

  const handleBack = () => {
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1)
    }
  }

  const handleConfirm = () => {
    setIsPending(true)
    submitRef.current()
  }

  const stepProps = {
    submitRef,
    onSuccess: handleStepSuccess,
    setIsPending,
  }

  const stepContent: Record<CreateStepKey, React.ReactNode> = {
    personal: <PersonalStep {...stepProps} />,
    contact: <ContactStep {...stepProps} />,
    address: <AddressStep {...stepProps} />,
    invoice: <InvoiceStep {...stepProps} />,
    policies: <PoliciesStep {...stepProps} />,
  }

  return (
    <ModalBottom
      open={open}
      close={close}
      hideBottomSpace
      modalProps={{ backdropClose: false }}
      body={
        <View>
          <View className="flex-row items-center justify-between p-8">
            <Button
              variant="outlined"
              size="sm"
              iconStart="chevron-left"
              onPress={handleBack}
              disabled={stepIndex === 0}
            />
            <StepDots
              count={steps.length}
              current={stepIndex}
              statuses={stepStatuses}
            />
            <Button
              variant="outlined"
              size="sm"
              iconStart="x"
              onPress={close}
            />
          </View>

          <View className="items-center gap-2 pb-12">
            <Typography variant="XLB" color={colors.text}>
              {t(step.titleKey as Parameters<typeof t>[0])}
            </Typography>
            {stepDescUsesFormatting ? (
              <FormattedText
                variant="MD"
                text={t(`modals:${stepDescKey}` as Parameters<typeof t>[0])}
                className="color-desc text-center"
                classNameBold="color-text font-text-semibold"
              />
            ) : (
              <Typography
                variant="MD"
                color={colors.text50}
                className="text-center"
              >
                {t(`modals:${stepDescKey}` as Parameters<typeof t>[0])}
              </Typography>
            )}
          </View>

          <View className="px-8">
            {stepKey === 'policies' || stepKey === 'contact' ? (
              stepContent[step.key]
            ) : (
              <View className="overflow-hidden rounded-xl border border-black/10 bg-white">
                <View className="gap-8 p-8">{stepContent[step.key]}</View>
              </View>
            )}
          </View>

          <View className="relative items-center px-8 py-16">
            <Gradient
              colors={[colors.white0, colors.white]}
              start={[0, 0]}
              end={[0, 1]}
            />
            <Button
              variant="outlined"
              label={t('ui:confirm')}
              iconEnd="arrow-narrow-right"
              loading={isPending}
              onPress={handleConfirm}
            />
          </View>
        </View>
      }
    />
  )
}
